import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Send, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";

export default function PaymentDialog({ open, onOpenChange, card, onDone }) {
  const [merchant, setMerchant] = useState("");
  const [amount, setAmount] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const reset = () => {
    setMerchant("");
    setAmount("");
    setPassword("");
    setConfirmed(null);
  };

  const handlePay = async () => {
    if (!merchant || !amount || !password) {
      toast.error("Fill all fields");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post(`/cards/${card.id}/pay`, {
        merchant,
        amount: parseFloat(amount),
        password,
      });
      setConfirmed(res.data.transaction);
      onDone(res.data.new_balance);
      toast.success("Payment confirmed by sealed signature");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="bg-[#121216] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display font-black tracking-tight flex items-center gap-2">
            <Send className="w-5 h-5 text-[#FF007F]" /> Make a Payment
          </DialogTitle>
          <DialogDescription className="text-[#A0A0AB]">
            Your card password authorizes the sealed 16-digit signature to confirm the transaction.
          </DialogDescription>
        </DialogHeader>

        {confirmed ? (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="py-4 text-center space-y-4">
            <CheckCircle2 className="w-14 h-14 text-[#00FF9D] mx-auto" />
            <div>
              <p className="font-display font-black text-2xl">${confirmed.amount.toFixed(2)}</p>
              <p className="text-[#A0A0AB] text-sm">paid to {confirmed.merchant}</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <div className="flex items-center justify-between mb-1">
                <p className="font-mono-sig text-[9px] tracking-[0.3em] uppercase text-[#A0A0AB]">Unique Signature</p>
                <span data-testid="confirmation-nonce" className="font-mono-sig text-[9px] tracking-[0.2em] uppercase text-[#00FF9D]">
                  Nonce #{confirmed.nonce}
                </span>
              </div>
              <p data-testid="confirmation-code" className="font-mono-sig text-sm tracking-[0.2em] text-[#00F0FF] break-all">
                {confirmed.confirmation_code}
              </p>
            </div>
            <Button
              data-testid="payment-done-button"
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
              className="w-full h-11 bg-white text-black hover:bg-white/90 rounded-xl font-semibold"
            >
              Done
            </Button>
          </motion.div>
        ) : (
          <div className="space-y-4 pt-2">
            <div className="rounded-xl border border-white/10 bg-white/5 p-3 flex items-center justify-between">
              <span className="text-[#A0A0AB] text-sm">Available balance</span>
              <span className="font-mono-sig font-semibold text-white">${card.balance.toFixed(2)}</span>
            </div>
            <div className="space-y-2">
              <Label className="text-[#A0A0AB] text-xs uppercase tracking-wider">Merchant</Label>
              <Input
                data-testid="pay-merchant-input"
                placeholder="e.g. Nova Coffee"
                value={merchant}
                onChange={(e) => setMerchant(e.target.value)}
                className="bg-[#1A1A20] border-white/10 text-white h-11"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#A0A0AB] text-xs uppercase tracking-wider">Amount (USD)</Label>
              <Input
                data-testid="pay-amount-input"
                type="number"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="bg-[#1A1A20] border-white/10 text-white h-11 font-mono-sig"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#A0A0AB] text-xs uppercase tracking-wider">Card password</Label>
              <Input
                data-testid="pay-password-input"
                type="password"
                placeholder="Confirm with password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-[#1A1A20] border-white/10 text-white h-11 font-mono-sig"
              />
            </div>
            <Button
              data-testid="pay-submit-button"
              onClick={handlePay}
              disabled={loading}
              className="w-full h-12 bg-[#FF007F] text-white hover:bg-[#FF007F]/90 font-semibold rounded-xl"
            >
              <ShieldCheck className="w-4 h-4 mr-2" /> {loading ? "Confirming…" : "Sign & Pay"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
